export interface Tomo {
  num: string
  title: string
  range: string
}
export interface Bono {
  num: string
  name: string
  /** Beneficio en una línea. */
  role: string
  /** Portada oficial del bono (1:1). */
  cover: string
}
export interface Testimonial {
  /** Puntaje sobre 5; admite medios puntos. */
  rating: number
  title: string
  quote: string
  author: string
}
export interface Faq {
  q: string
  a: string
  /** Segundo párrafo opcional de la respuesta. */
  a2?: string
  /** Enlace de referencia opcional al final de la respuesta. */
  link?: { label: string; href: string }
}

/**
 * Formato monetario único de la landing: siempre "$00.000".
 */
const CURRENT_PRICE = 'US$ 19'

export const PRICE = {
  previous: 'US$ 24',
  current: CURRENT_PRICE,
  /** Microcopy de precalificación junto a los CTA de compra. */
  note: `${CURRENT_PRICE} USD · Pago único · Acceso digital inmediato · Sin suscripción`,
} as const

/** Línea de confianza bajo el botón principal de compra. */
export const TRUST_LINE = '🔒 Pago seguro · Producto 100% digital · Acceso inmediato'

/** Con información dispersa → Con las 200 fórmulas. */
export const TRANSFORM_BEFORE: string[] = [
  'No sabés qué proporciones usar.',
  'El jabón queda opaco o transpira.',
  'Se rompe al desmoldar o pierde el aroma.',
  'Gastás insumos probando a ciegas.',
]

export const TRANSFORM_AFTER: string[] = [
  'Fórmulas con cantidades exactas.',
  'Sabés qué base y qué aditivos usar.',
  'Temperaturas y procedimiento paso a paso.',
  'Detectás y corregís errores.',
]

/** Criterios con los que se elige qué fórmula fabricar dentro de la biblioteca. */
export const LIBRARY_CRITERIA: string[] = [
  'Dificultad',
  'Insumos',
  'Inversión',
  'Estilo',
  'Tipo de piel',
  'Ocasión',
  'Producción en tandas',
  'Formato de venta',
]

/** Las 10 familias que agrupan las 200 fórmulas. */
export const SOAP_FAMILIES: string[] = [
  'Uso Diario & Piel Sensible',
  'Glicerina Transparente & Capas',
  'Exfoliantes & Avena',
  'Botánicos con Hierbas y Flores',
  'Cuidado Específico & Bienestar',
  'Aromaterapia, Spa & Relax',
  'Decorativos & Moldes de Fantasía',
  'Souvenirs & Producción en Serie',
  'Jabones de Estación & Regalos',
  'Packs y Líneas para Vender',
]

/** Los 4 tomos de la colección. */
export const TOMOS: Tomo[] = [
  { num: '1', title: 'Jabones para el Día a Día', range: 'Fórmulas 001–050' },
  { num: '2', title: 'Jabones Especiales y Terapéuticos', range: 'Fórmulas 051–100' },
  { num: '3', title: 'Jabones Creativos y Decorativos', range: 'Fórmulas 101–150' },
  { num: '4', title: 'Jabones para Ocasiones y Negocio', range: 'Fórmulas 151–200' },
]

/** Lo que contiene cada ficha de fórmula. */
export const FORMULA_FIELDS: string[] = [
  'Insumos',
  'Cantidades y proporciones',
  'Base de glicerina',
  'Molde y formato',
  'Aromas y esencias',
  'Colorantes y aditivos',
  'Temperaturas',
  'Paso a paso',
  'Desmolde y reposo',
  'Terminación',
  'Errores y correcciones',
  'Consejo de venta',
]

/** Los 5 bonos incluidos gratis con la compra. */
export const BONOS: Bono[] = [
  {
    num: '1',
    name: 'Calculadora Simple de Costos y Precio Sugerido para Jabones Artesanales',
    role: 'Calculá cuánto te cuesta producir cada jabón y definí un precio con mayor claridad para no vender a ciegas.',
    cover: 'https://cdn.magicpatterns.com/uploads/jufcU9Pq7gE7hhCWm7KbRs/bono_1.png',
  },
  {
    num: '2',
    name: 'Guía de Primeras Ventas: Cómo Vender Tus Primeros Jabones Desde Casa',
    role: 'Pasá de “ya hice mis jabones” a empezar a ofrecerlos, armar combos y conseguir tus primeras clientas.',
    cover: 'https://cdn.magicpatterns.com/uploads/6FzLc1tDfTjiGvhEPVJwJG/bono_2.png',
  },
  {
    num: '3',
    name: 'Instagram Artesanal: Perfil Listo para Mostrar y Vender Jabones',
    role: 'Armá un perfil claro, atractivo y profesional para mostrar tus jabones y facilitar que te hagan pedidos.',
    cover: 'https://cdn.magicpatterns.com/uploads/eYcuubMQ5ZxVYbuXQVK9Di/bono_3.png',
  },
  {
    num: '4',
    name: 'Mapa de Insumos y Proveedores por País para Jabones de Glicerina',
    role: 'Qué comprar, cómo se llama en tu país y dónde buscarlo: España, México, Estados Unidos, Chile, Colombia, Perú, Uruguay y más.',
    cover: 'https://cdn.magicpatterns.com/uploads/b9CSuGMTN88WY4CtQoyQeV/bono_4.png',
  },
  {
    num: '5',
    name: 'Plan de Acción 48 Horas: De Cero a Tu Primer Jabón Terminado',
    role: 'Seguí una ruta práctica paso a paso para pasar de elegir una fórmula a tener tu primer jabón terminado.',
    cover: 'https://cdn.magicpatterns.com/uploads/4zmYQrAoabK4WEbQWEE571/bono_5.png',
  },
]

/** Opiniones de compradoras, justo antes del bloque de oferta. */
export const TESTIMONIALS: Testimonial[] = [
  {
    rating: 5,
    title: 'Mucho más completo de lo que esperaba',
    quote:
      'Lo compré porque quería aprender a hacer jabones lindos para regalar y terminé pensando seriamente en venderlos. Hay muchísimas ideas y combinaciones. Ya tengo varios favoritos.',
    author: 'Laura, 42 años',
  },
  {
    rating: 5,
    title: 'Por fin me animé a empezar',
    quote:
      'Siempre veía jabones artesanales hermosos en redes y pensaba que eran dificilísimos. Con las explicaciones del libro hice los primeros en casa y quedaron preciosos.',
    author: 'Marcela, 37 años',
  },
  {
    rating: 5,
    title: 'Me abrió la cabeza para vender',
    quote:
      'Yo ya hacía jabones, pero siempre terminaba ofreciendo los mismos modelos. Lo que más me gustó fue la variedad porque pude pensar un catálogo mucho más atractivo.',
    author: 'Andrea, emprendedora',
  },
  {
    rating: 4.5,
    title: 'Hermosas ideas',
    quote:
      'Hay tantas fórmulas que todavía no llegué ni a probar la mitad. Compré el material para hacer algo como hobby y ahora mis amigas me están preguntando cuánto cobro por los jabones 😂.',
    author: 'Sofía, 34 años',
  },
]

/** Resumen corto de la oferta (no repetir la lista completa de contenidos). */
export const OFFER_INCLUDES: string[] = [
  '4 tomos digitales',
  '200 fórmulas paso a paso',
  '5 libros extra de regalo',
  'Acceso digital inmediato',
]

export const FAQS: Faq[] = [
  {
    q: '¿Necesito experiencia haciendo jabones?',
    a: 'No. El material empieza por los fundamentos de la base de glicerina y avanza hacia técnicas más elaboradas.',
  },
  {
    q: '¿Necesito equipamiento profesional?',
    a: 'No. Muchas fórmulas se resuelven con moldes, utensilios e insumos básicos de fabricación artesanal.',
  },
  {
    q: '¿Se trabaja con sosa cáustica?',
    a: 'No. Todas las fórmulas parten de base de glicerina lista para fundir y moldear, sin saponificación desde cero.',
  },
  {
    q: '¿Tengo que hacer las 200 fórmulas?',
    a: 'No. Funcionan como una biblioteca de consulta: elegís las que se adaptan a lo que querés fabricar.',
  },
  {
    q: '¿Los 5 libros extra están incluidos?',
    a: 'Sí. Los 5 libros de regalo vienen gratis con la compra, sin costo adicional.',
  },
  {
    q: '¿Cómo lo recibo?',
    a: 'Es un producto digital. Después de la compra recibís el acceso para descargar los 4 tomos y los 5 libros extra.',
  },
  {
    q: '¿El pago es único?',
    a: 'Sí. US$ 19 en un único pago, sin suscripciones.',
  },
  {
    q: '¿Puedo vender los jabones que aprenda a hacer?',
    a: 'El material puede servirte como base para desarrollar tus propios jabones y un emprendimiento. Si decidís comercializarlos, deberás cumplir con las habilitaciones, requisitos sanitarios y demás normativa aplicable según tu lugar de residencia y el tipo de producto.',
    a2: 'En Argentina, podés consultar los requisitos vigentes directamente en ANMAT.',
    link: {
      label: 'Consultar información oficial de ANMAT sobre cosméticos',
      href: 'https://www.argentina.gob.ar/anmat/regulados/cosmeticos',
    },
  },
]
